'use client';
import React from 'react';
import Script from 'next/script';
import { SessionContext } from '@benzinga/session-context';
import { AuthenticationManager } from '@benzinga/session';
import { checkDeviceType } from '@benzinga/device-utils';
import { handleCommentsAnalyticsEvent, rootURL } from '../utils';
import { TrackingManager } from '@benzinga/tracking-manager';
import { runningClientSide } from '@benzinga/utils';

declare global {
  interface Window {
    Coral: {
      createStreamEmbed: (createStreamEmbedProps: CreateStreamEmbedProps) => any;
    };
  }
}

interface CreateStreamEmbedProps {
  accessToken?: string;
  autoRender: boolean;
  containerClassName?: string;
  events: (events: CreateStreamEmbedEvents) => void;
  id: string;
  rootURL: string;
  storyID?: string;
  storyURL?: string;
}

interface CreateStreamEmbedEvents {
  on: (eventName: string, eventAction: () => void) => void;
  onAny: (eventAction: (eventName: string, eventData: any) => void) => void;
}

export type PageType = 'Article' | 'Quote' | 'Offering';

export interface CommentsEmbedScriptProps {
  id?: number | string;
  url: string;
  type?: PageType;
  isInjected: boolean;
}

//const isProduction = process.env.NODE_ENV !== 'production'; // TODO: Change this later
//const rootURL = isProduction ? 'https://comments.benzinga.com' : 'https://comments.zingbot.bz';

export const CommentsEmbedScript: React.FC<CommentsEmbedScriptProps> = ({
  id,
  isInjected: isInjectedFromProps,
  type,
  url,
}) => {
  const session = React.useContext(SessionContext);
  //const [coralEmbed, setCoralEmbed] = React.useState<{ login: (ssoToken: string) => void } | null>(null);
  //const [hasRan, setHasRan] = React.useState(false);
  const [isInjected, setIsInjected] = React.useState(isInjectedFromProps);

  React.useEffect(() => {
    if (isInjected) return;
    if (isInjectedFromProps) {
      setIsInjected(true);
    }
  }, [isInjected, isInjectedFromProps]);

  const handleLoginAnalyticsEvent = () => {
    type && handleCommentsAnalyticsEvent(type, 'login-click', session);
  };

  const handleEmitCommentCreatedAnalyticsEvent = () => {
    type && handleCommentsAnalyticsEvent(type, 'comment-created', session);
  };

  const onLoad = () => {
    const deviceType = checkDeviceType().isMobile() ? 'mobile' : 'desktop';
    const coralEmbed = window.Coral.createStreamEmbed({
      autoRender: true,
      containerClassName: 'coral-comments',
      events: function (events) {
        events.on('loginPrompt', function () {
          handleLoginAnalyticsEvent();
          window.location.href = `/login?next=${window.location.href}&utm_source=benzinga.com&utm_campaign=comments&utm_medium=${deviceType}`;
        });
        events.onAny(function (eventName) {
          if (eventName === 'createComment.success') {
            handleEmitCommentCreatedAnalyticsEvent();
          }
        });
      },
      id: 'coral_thread',
      // https://docs.coralproject.net
      rootURL,
      storyID: id ? String(id) : undefined,
      storyURL: url,
    });
    const handleLoginToCoral = () => {
      const coralJWT = session.getManager(AuthenticationManager).getCoralJWT() || null;
      coralJWT && coralEmbed?.login(coralJWT);
    };
    handleLoginToCoral();
  };

  // const handleRemoveNav = () => {
  //   const shadowRoot = document.querySelector('#coral-shadow-container')?.shadowRoot;
  //   const navElement = shadowRoot?.querySelector('#coral > main > nav') as HTMLElement;
  //   if (navElement) {
  //     navElement.style.display = 'none';
  //   }
  // };

  if (!isInjected) return null;

  return (
    <Script
      async={false}
      defer={true}
      id="coral-comments-script"
      onLoad={onLoad}
      src={`${rootURL}/assets/js/embed.js`}
      strategy="lazyOnload"
      type="text/javascript"
    />
  );
};

export default CommentsEmbedScript;
